import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useEffect, useState} from 'react';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {ItemOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-opacity-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {VectorColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/vector-color-option';
import {ItemShadowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-shadow-option';
import {InformationLinkOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/information-link-option';
import {ItemInteractivityLinkOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-interactivity-link-option';
import {ContextualMenu} from '@Components/contextual-menu';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {CustomShadowColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/custom-shadow-color-panel';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {DefaultEditingOptionsHeader} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-editing-options-header';
import {VectorFillColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/vector-fill-color-panel';
import {VectorStrokeColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/vector-stroke-color-panel';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {RectangleRoundnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/rectangle-roundness-option/rectangle-roundness-option';
import {VectorOutlineOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/vector-outline-option/vector-outline-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {VectorItemPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-vector-options/poster-editing-vector-options.types';
import {useIsOpenedVectorPanelValid} from '@Hooks/poster-editor/useIsOpenedVectorPanelValid';
import {AnimationPanelType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {ItemAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-option/item-animation-option';
import {ItemIntroAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-intro-animation-panel/item-intro-animation-panel';
import {ItemOutroAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-outro-animation-panel/item-outro-animation-panel';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';
import {useGetKeyFromShadowOfActiveItem} from '@Hooks/poster-editor/useGetKeyFromShadowOfActiveItem';
import {ShadowOrGlowType} from '@postermywall/fabricjs-2';

export function PosterEditingRectangleOptions(): ReactElement {
  const activeItemId = useSingleActivePosterItemId();
  const shadowOrGlowType = useGetKeyFromShadowOfActiveItem('shadowOrGlowType');
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [currentlyOpenedPanel, setCurrentlyOpenedPanel] = useState(VectorItemPanels.NONE);
  useIsOpenedVectorPanelValid(currentlyOpenedPanel, setCurrentlyOpenedPanel);

  if (!activeItem) {
    throw new Error(`Active item is null in poster editing rectangle options`);
  }

  useEffect((): void => {
    if (shadowOrGlowType !== ShadowOrGlowType.CUSTOM_SHADOW && currentlyOpenedPanel === VectorItemPanels.CUSTOM_SHADOW) {
      setCurrentlyOpenedPanel(VectorItemPanels.NONE);
    }
  }, [shadowOrGlowType]);

  const isSubPanelOpened = (): boolean => {
    return currentlyOpenedPanel !== VectorItemPanels.NONE;
  };

  const onBackButtonFromColorPanel = (): void => {
    setCurrentlyOpenedPanel(VectorItemPanels.NONE);
  };

  const getHeaderText = (): string => {
    if (currentlyOpenedPanel === VectorItemPanels.INTRO_ANIMATION) {
      return window.i18next.t('pmwjs_entrance_animation');
    } else if (currentlyOpenedPanel === VectorItemPanels.OUTRO_ANIMATION) {
      return window.i18next.t('pmwjs_exit_animation');
    } else {
      return window.i18next.t('pmwjs_color');
    }
  };

  const getHeader = (): ReactElement => {
    if (isSubPanelOpened()) {
      return (
        <ContextualMenuHeader id="rectangle-sub-panel">
          <ContextualMenuTextHeader text={getHeaderText()} showBackButton onBackButton={onBackButtonFromColorPanel} />
        </ContextualMenuHeader>
      );
    }

    return <DefaultEditingOptionsHeader currentItemHeaderTab={currentItemHeaderTab} onChangeSelectedHeaderItemTab={setCurrentItemHeaderTab} />;
  };

  const getItemEditOptions = (): ReactElement => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls />
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(type: AnimationPanelType): void => {
            setCurrentlyOpenedPanel(type === AnimationPanelType.INTRO_ANIMATION_STYLES ? VectorItemPanels.INTRO_ANIMATION : VectorItemPanels.OUTRO_ANIMATION);
          }}
        />
        <VectorColorOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(VectorItemPanels.COLOR_FILL);
          }}
        />
        <VectorOutlineOption
          onClickColorOption={(): void => {
            setCurrentlyOpenedPanel(VectorItemPanels.OUTLINE);
          }}
        />
        <RectangleRoundnessOption />
        <ItemShadowOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(VectorItemPanels.CUSTOM_SHADOW);
          }}
        />
        <ContextualMenuHeadingOption id="vector-fill-interactivity-option" text={window.i18next.t('pmwjs_interactivity')} />
        <ItemInteractivityLinkOption />
        <InformationLinkOption />
      </>
    );
  };

  const getSubPanel = (): ReactElement => {
    switch (currentlyOpenedPanel) {
      case VectorItemPanels.COLOR_FILL:
        return (
          <VectorFillColorPanel
            closePanel={(): void => {
              setCurrentlyOpenedPanel(VectorItemPanels.NONE);
            }}
          />
        );
      case VectorItemPanels.CUSTOM_SHADOW:
        return <CustomShadowColorPanel />;
      case VectorItemPanels.OUTLINE:
        return <VectorStrokeColorPanel />;
      case VectorItemPanels.INTRO_ANIMATION:
        return <ItemIntroAnimationPanel />;
      case VectorItemPanels.OUTRO_ANIMATION:
        return <ItemOutroAnimationPanel />;
      default:
        throw new Error(`unhandled sub panel ${currentlyOpenedPanel} for vector items`);
    }
  };

  const getPositionOptions = (): ReactElement => {
    return <ItemPositionContextualOptions />;
  };

  const getOptions = (): ReactElement => {
    if (isSubPanelOpened()) {
      return getSubPanel();
    }

    switch (currentItemHeaderTab) {
      case ItemHeaderTab.EDIT:
        return getItemEditOptions();
      case ItemHeaderTab.POSITION:
        return getPositionOptions();
      default:
        throw new Error('unhandled properties for this item');
    }
  };

  const getKey = (): string => {
    if (!isSubPanelOpened()) {
      return currentItemHeaderTab === ItemHeaderTab.POSITION ? ContextualMenuContentKeys.RECTANGLE_DEFAULT_POSITION : ContextualMenuContentKeys.RECTANGLE_DEFAULT_EDIT;
    }

    return '';
  };

  return useMemo((): ReactElement => {
    return (
      <ContextualMenu contentKey={getKey()} id="rectangle-contextual-options" includeLeftRightPadding={!isSubPanelOpened()} header={getHeader()}>
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentlyOpenedPanel, currentItemHeaderTab]);
}
